import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Skeleton,
} from "@asm/ui";
import { Users as UsersIcon, AlertCircle } from "lucide-react";
import { adminApi, User } from "../lib/api";

const roleStyles: Record<string, string> = {
  admin: "bg-red-500/10 text-red-400 border-red-500/30",
  member: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  viewer: "bg-muted text-muted-foreground border-border",
};

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await adminApi.listUsers();
        setUsers(response.users);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load users");
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  return (
    <div className="tactical-grid space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <UsersIcon className="h-8 w-8 text-red-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Users</h1>
            <p className="text-muted-foreground">View all users across organizations</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="animate-fade-in-up rounded-lg border border-red-500/30 bg-red-500/5 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Users Table */}
      <Card className="animate-fade-in-up stagger-1 border-border bg-card/50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-6 w-1 rounded-full bg-red-500" />
            <div>
              <CardTitle>All Users</CardTitle>
              <CardDescription>Platform-wide user management</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-3 rounded-lg bg-muted/30 border border-border mb-4">
                <UsersIcon className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground text-sm">No users found</p>
            </div>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Name</TableHead>
                    <TableHead className="font-semibold">Organization</TableHead>
                    <TableHead className="font-semibold">Role</TableHead>
                    <TableHead className="font-semibold">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow
                      key={user.id}
                      className={`hover:bg-muted/50 transition-colors animate-fade-in-up stagger-${index + 1}`}
                    >
                      <TableCell className="font-mono text-sm">{user.email}</TableCell>
                      <TableCell className="font-medium">{user.name || "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{user.org_name}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={roleStyles[user.role] || roleStyles.viewer}
                        >
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
